import { useEffect, useState } from "react";
import { Sheet } from "./Sheet";

/**
 * #42 홈 화면에 추가하기. Android Chrome keeps the `beforeinstallprompt`
 * event and fires it from our button; iOS Safari gets the three-step share
 * sheet guide; in-app browsers (카톡·인스타 등) are asked to reopen in
 * Safari/Chrome first. Hidden when already installed or on Apps in Toss.
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeinstallprompt_event
 */

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== "undefined") {
	window.addEventListener("beforeinstallprompt", (event) => {
		event.preventDefault();
		deferred = event as BeforeInstallPromptEvent;
		for (const listener of listeners) listener();
	});
	window.addEventListener("appinstalled", () => {
		deferred = null;
		for (const listener of listeners) listener();
	});
}

const DISMISS_KEY = "sobidogam:installPromptDismissedAt";
const DAY = 86_400_000;

export type InstallPlatform =
	| "installed"
	| "android"
	| "ios"
	| "in_app"
	| "other";

export function installPlatform(): InstallPlatform {
	if (typeof window === "undefined") return "other";
	const standalone =
		window.matchMedia?.("(display-mode: standalone)").matches ||
		(navigator as Navigator & { standalone?: boolean }).standalone === true;
	if (standalone) return "installed";
	const ua = navigator.userAgent;
	if (/KAKAOTALK|Instagram|FBAN|FBAV|NAVER\(inapp|Line\/|DaumApps/i.test(ua))
		return "in_app";
	if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
	if (deferred) return "android";
	return "other";
}

function readDismissed(): number {
	try {
		return Number(window.localStorage.getItem(DISMISS_KEY) ?? "0");
	} catch {
		return 0;
	}
}

/** True once per 30 days after the first record, when there is a real path. */
export function shouldOfferInstall(): boolean {
	const platform = installPlatform();
	if (platform === "installed" || platform === "other") return false;
	return Date.now() - readDismissed() > 30 * DAY;
}

function rememberDismiss() {
	try {
		window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
	} catch {
		// Private mode: asking again later is acceptable.
	}
}

function useInstallPlatform(): InstallPlatform {
	const [platform, setPlatform] = useState(installPlatform);
	useEffect(() => {
		const update = () => setPlatform(installPlatform());
		listeners.add(update);
		return () => {
			listeners.delete(update);
		};
	}, []);
	return platform;
}

export function InstallGuide({
	onClose,
	onOutcome,
}: {
	onClose: () => void;
	onOutcome: (outcome: string, platform: InstallPlatform) => void;
}) {
	const platform = useInstallPlatform();
	const [notice, setNotice] = useState("");
	const close = () => {
		rememberDismiss();
		onOutcome("dismissed", platform);
		onClose();
	};
	return (
		<Sheet title="홈 화면에 소비도감 두기" onClose={close}>
			<p>
				결제 알림이 뜨면 홈 화면 아이콘에서 바로 붙여 넣을 수 있어요. 앱 설치
				없이 아이콘만 생겨요.
			</p>
			{platform === "android" ? (
				<button
					className="button full-width"
					type="button"
					onClick={async () => {
						if (!deferred) {
							setNotice(
								"이 브라우저에서는 메뉴의 '홈 화면에 추가'를 눌러 주세요.",
							);
							return;
						}
						await deferred.prompt();
						const choice = await deferred.userChoice;
						deferred = null;
						onOutcome(choice.outcome, platform);
						if (choice.outcome === "accepted") onClose();
						else setNotice("나중에 설정에서 다시 추가할 수 있어요.");
					}}
				>
					홈 화면에 추가
				</button>
			) : null}
			{platform === "ios" ? (
				<ol className="install-steps">
					<li>
						<span className="install-step-icon" aria-hidden="true">
							<svg
								viewBox="0 0 24 24"
								width="24"
								height="24"
								aria-hidden="true"
							>
								<path
									d="M12 3v12M7 8l5-5 5 5M5 13v7h14v-7"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.8"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
						Safari 아래쪽 <b>공유</b> 버튼을 눌러요.
					</li>
					<li>
						<span className="install-step-icon" aria-hidden="true">
							<svg
								viewBox="0 0 24 24"
								width="24"
								height="24"
								aria-hidden="true"
							>
								<rect
									x="4"
									y="4"
									width="16"
									height="16"
									rx="3"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.8"
								/>
								<path
									d="M12 8v8M8 12h8"
									stroke="currentColor"
									strokeWidth="1.8"
									strokeLinecap="round"
								/>
							</svg>
						</span>
						목록을 올려 <b>홈 화면에 추가</b>를 골라요.
					</li>
					<li>
						<span className="install-step-icon" aria-hidden="true">
							<svg
								viewBox="0 0 24 24"
								width="24"
								height="24"
								aria-hidden="true"
							>
								<path
									d="M5 12l4 4 10-10"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.8"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
						오른쪽 위 <b>추가</b>를 누르면 끝이에요.
					</li>
				</ol>
			) : null}
			{platform === "in_app" ? (
				<p className="notice">
					카카오톡·인스타그램 안 브라우저에서는 홈 화면에 둘 수 없어요. 오른쪽
					위 메뉴에서 <b>다른 브라우저로 열기</b>(Safari·Chrome)를 누른 뒤 다시
					시도해 주세요.
				</p>
			) : null}
			{platform === "installed" ? (
				<p className="notice">이미 홈 화면에 있어요.</p>
			) : null}
			{platform === "other" ? (
				<p className="notice">
					브라우저 메뉴에서 <b>홈 화면에 추가</b> 또는 <b>앱 설치</b>를 눌러
					주세요.
				</p>
			) : null}
			{notice ? <output className="form-note">{notice}</output> : null}
			<button className="quiet-button" type="button" onClick={close}>
				다음에 할게요
			</button>
		</Sheet>
	);
}
